import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Empty,
  InputNumber,
  Modal,
  Progress,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import {
  AimOutlined,
  CompassOutlined,
  EditOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import ProjectPageShell, { useProjectContext } from "../shared/ProjectPageShell";
import StatCard from "../shared/StatCard";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import { driveChainApis } from "../../../api/ActivityExecution";
import { useWbsRecordName } from "../shared/useWbsCodes";
import { fmtQty } from "./executionNorm";
import "./execution.css";
import { chLabel, classColor } from "./driveChainShared";

// Drives — the chainage ledger's face of the tunnel (D1, read-only).
//
// One row per drive: where the face stands tonight, how much of the design
// length is covered, and how the covered ground splits into class reaches.
// Everything is COMPUTED from the daily stretches the site already captures —
// nothing on this page is typed except the design range, which is WBS data
// (planning's two numbers per face, no drive master).

// The strip chart: the drive as one bar, reaches colored by class, gaps
// hatched, the face marked. Scaled by the design range when anchored, else by
// the covered extent.
const DriveStrip = ({ detail }: { detail: any }) => {
  const reaches: any[] = detail.reaches || [];
  const gaps: any[] = detail.gaps || [];
  const hasDesign = detail.anchoredBy === "design";
  const points = [
    ...(hasDesign
      ? [Number(detail.startChainage), Number(detail.endChainage)]
      : []),
    ...reaches.flatMap((r) => [Number(r.fromChainage), Number(r.toChainage)]),
  ];
  if (!points.length) return <Empty description="Nothing captured yet" />;
  const lo = Math.min(...points);
  const hi = Math.max(...points);
  const span = hi - lo || 1;
  const W = 920;
  const x = (ch: number) => 20 + ((ch - lo) / span) * (W - 40);
  const face = detail.faceChainage === "" ? null : Number(detail.faceChainage);

  return (
    <svg
      viewBox={`0 0 ${W} 86`}
      style={{ width: "100%", display: "block" }}
      role="img"
      aria-label="Drive strip chart: class reaches, gaps and the face position along the chainage"
    >
      <rect x={20} y={22} width={W - 40} height={24} rx={4} fill="#eef1f7" stroke="#dfe4ee" />
      {gaps.map((gap, i) => {
        const gx = x(Math.min(Number(gap.fromChainage), Number(gap.toChainage)));
        const gw = Math.max(
          2,
          Math.abs(x(Number(gap.toChainage)) - x(Number(gap.fromChainage)))
        );
        return (
          <rect
            key={`g${i}`}
            x={gx}
            y={22}
            width={gw}
            height={24}
            fill="#fdeeec"
            stroke="#c23934"
            strokeDasharray="3 2"
          />
        );
      })}
      {reaches.map((reach, i) => {
        const rx = x(Math.min(Number(reach.fromChainage), Number(reach.toChainage)));
        const rw = Math.max(
          1.5,
          Math.abs(x(Number(reach.toChainage)) - x(Number(reach.fromChainage)))
        );
        return (
          <g key={`r${i}`}>
            <rect x={rx} y={22} width={rw} height={24} fill={classColor(reach.classLabel)}>
              <title>{`${reach.classLabel || reach.slotKey} · CH ${reach.fromChainage} → ${reach.toChainage} · ${fmtQty(reach.length)} m`}</title>
            </rect>
          </g>
        );
      })}
      {face != null && (
        <g>
          <line x1={x(face)} y1={12} x2={x(face)} y2={56} stroke="#2557d6" strokeWidth={2.5} />
          <text
            x={x(face)}
            y={70}
            textAnchor="middle"
            fontSize={11}
            fill="#2557d6"
            fontFamily="monospace"
          >
            face {Number(face).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          </text>
        </g>
      )}
      <text x={20} y={60} fontSize={10} fill="#8b95a7" fontFamily="monospace">
        {lo.toLocaleString("en-IN", { maximumFractionDigits: 1 })}
      </text>
      <text x={W - 20} y={60} textAnchor="end" fontSize={10} fill="#8b95a7" fontFamily="monospace">
        {hi.toLocaleString("en-IN", { maximumFractionDigits: 1 })}
      </text>
    </svg>
  );
};

// Expanded row: the strip plus the class-wise register for this drive.
const DriveDetail = ({ activityId }: { activityId: number }) => {
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    driveChainApis
      .get(activityId)
      .then((res) => {
        if (!cancelled && res?.status === true) setDetail(res.data);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [activityId]);

  if (loading) return <div style={{ padding: 12 }}>Loading chain…</div>;
  if (!detail) return <Empty description="Could not load the chain" />;

  const totalsByClass = new Map<string, number>();
  for (const reach of detail.reaches || []) {
    const key = reach.classLabel || reach.slotKey;
    totalsByClass.set(key, (totalsByClass.get(key) || 0) + Number(reach.length));
  }

  return (
    <div style={{ display: "grid", gap: 14, padding: "6px 4px 10px" }}>
      <DriveStrip detail={detail} />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {Array.from(totalsByClass.entries()).map(([label, total]) => (
          <Tag key={label} style={{ borderColor: classColor(label), color: classColor(label), background: "#fff" }}>
            {label}: {fmtQty(total)} m
          </Tag>
        ))}
        {(detail.gaps || []).map((gap: any, i: number) => (
          <Tag key={`gap${i}`} color="error" icon={<WarningOutlined />}>
            gap CH {gap.fromChainage} – {gap.toChainage} ({fmtQty(gap.length)} m)
          </Tag>
        ))}
      </div>
      <Table
        size="small"
        rowKey={(r: any) => `${r.fromChainage}-${r.toChainage}`}
        dataSource={detail.reaches || []}
        pagination={false}
        columns={[
          {
            title: "From CH",
            dataIndex: "fromChainage",
            render: (v: string) => <span className="exec-num">{v}</span>,
          },
          {
            title: "To CH",
            dataIndex: "toChainage",
            render: (v: string) => <span className="exec-num">{v}</span>,
          },
          {
            title: "Length",
            dataIndex: "length",
            render: (v: string) => <span className="exec-num">{fmtQty(v)} m</span>,
          },
          {
            title: "Class",
            dataIndex: "classLabel",
            render: (v: string, r: any) => (
              <Tag style={{ borderColor: classColor(v), color: classColor(v) }}>{v || r.slotKey}</Tag>
            ),
          },
          { title: "First day", dataIndex: "firstDate" },
          { title: "Last day", dataIndex: "lastDate" },
          { title: "Entries", dataIndex: "segmentCount", align: "right" as const },
        ]}
      />
    </div>
  );
};

const DrivesPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { projectId: contextProjectId, projectName } = useProjectContext();
  const projectId = Number(params.projectId) || contextProjectId;

  const [drives, setDrives] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [rangeDraft, setRangeDraft] = useState<{ start: number | null; end: number | null }>({
    start: null,
    end: null,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({ ...item, linkState: location.state }))
      )
    );
  }, [dispatch, location]);

  const load = useCallback(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    driveChainApis
      .list(projectId)
      .then((res) => {
        if (cancelled) return;
        if (res?.status !== true) {
          message.error(res?.message || "Could not load the drives");
          return;
        }
        setDrives(res.data?.drives || []);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  const driveLabel = useWbsRecordName(drives, "activityId", "activityName");

  const anchoredCount = drives.filter((d) => d.anchoredBy === "design").length;
  const openGaps = drives.reduce((sum, d) => sum + (d.gapCount || 0), 0);

  const openRangeEditor = (drive: any) => {
    setEditing(drive);
    setRangeDraft({
      start: drive.startChainage === "" ? null : Number(drive.startChainage),
      end: drive.endChainage === "" ? null : Number(drive.endChainage),
    });
  };

  const saveRange = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const res = await driveChainApis.setChainage({
        activityId: editing.activityId,
        startChainage: rangeDraft.start == null ? "" : String(rangeDraft.start),
        endChainage: rangeDraft.end == null ? "" : String(rangeDraft.end),
      });
      if (res?.status !== true) {
        message.error(res?.message || "Could not save the range");
        return;
      }
      message.success(res.data?.message || "Saved");
      // Captured ground outside the new range is a fact worth hearing about.
      if (res.data?.warning) message.warning(res.data.warning, 6);
      setEditing(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: "Drive",
        dataIndex: "activityName",
        render: (_: any, drive: any) => (
          <div>
            <div style={{ fontWeight: 600 }}>{driveLabel(drive)}</div>
            <div className="exec-muted">
              {drive.anchoredBy === "design"
                ? `${chLabel(drive.startChainage)} → ${chLabel(drive.endChainage)}`
                : drive.anchoredBy === "capture"
                  ? "no design chainage — anchored by capture"
                  : "not started"}
            </div>
          </div>
        ),
      },
      {
        title: "Face at",
        dataIndex: "faceChainage",
        align: "right" as const,
        render: (v: string) => <span className="exec-num">{chLabel(v)}</span>,
      },
      {
        title: "Covered",
        dataIndex: "coveredLength",
        align: "right" as const,
        render: (v: string) => <span className="exec-num">{fmtQty(v)} m</span>,
      },
      {
        title: "% by chainage",
        dataIndex: "percentByChainage",
        width: 170,
        render: (v: string) =>
          v === "" ? (
            <span className="exec-muted">—</span>
          ) : (
            <Progress percent={Number(v)} size="small" />
          ),
      },
      {
        title: "Reaches",
        dataIndex: "reachCount",
        align: "right" as const,
      },
      {
        title: "Gaps",
        dataIndex: "gapCount",
        align: "right" as const,
        render: (v: number) =>
          v ? <Tag color="error">{v}</Tag> : <span className="exec-muted">0</span>,
      },
      { title: "Last capture", dataIndex: "lastCaptureDate", render: (v: string) => v || "—" },
      {
        title: "",
        key: "actions",
        width: 56,
        render: (_: any, drive: any) => (
          <Tooltip title="Set design range">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                openRangeEditor(drive);
              }}
            />
          </Tooltip>
        ),
      },
    ],
    [driveLabel]
  );

  return (
    <>
      <ProjectPageShell
        headTitle="Drives"
        breadcrumb={[
          { label: projectName, onClick: () => navigate(-1) },
          { label: "Drives" },
        ]}
        eyebrow="Execution & Actuals"
        title="Drives"
        onBack={() => navigate(-1)}
        stats={
          <>
            <StatCard label="Drives" value={drives.length} icon={<CompassOutlined />} />
            <StatCard
              label="Anchored by design"
              value={`${anchoredCount}/${drives.length}`}
              icon={<AimOutlined />}
            />
            <StatCard label="Open gaps" value={openGaps} icon={<WarningOutlined />} />
          </>
        }
      >
        <Table
          rowKey="activityId"
          dataSource={drives}
          columns={columns}
          loading={loading}
          pagination={false}
          expandable={{
            expandedRowRender: (drive: any) => (
              <DriveDetail activityId={drive.activityId} />
            ),
            rowExpandable: (drive: any) => drive.reachCount > 0 || drive.anchoredBy === "design",
          }}
          locale={{
            emptyText: (
              <Empty description="No drives yet — configure execution norms or set a design range" />
            ),
          }}
        />
      </ProjectPageShell>

      <Modal
        title={editing ? `Design range — ${editing.activityName}` : ""}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={saveRange}
        okText="Save range"
        confirmLoading={saving}
      >
        <p className="exec-muted" style={{ marginTop: 0 }}>
          The drive's design chainage — planning data from the WBS. Direction
          derives from start vs end; clear both to return the drive to
          capture-anchored mode.
        </p>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <InputNumber
            style={{ flex: 1 }}
            placeholder="Start CH"
            value={rangeDraft.start}
            onChange={(v) => setRangeDraft((prev) => ({ ...prev, start: v as number | null }))}
            step={0.001}
          />
          <span>→</span>
          <InputNumber
            style={{ flex: 1 }}
            placeholder="End CH"
            value={rangeDraft.end}
            onChange={(v) => setRangeDraft((prev) => ({ ...prev, end: v as number | null }))}
            step={0.001}
          />
        </div>
      </Modal>
    </>
  );
};

export default DrivesPage;
